import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { AccountType, User } from "@/types/user";
import { Organization } from "@/types/organization";
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Button,
    Divider,
} from "@heroui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { boolean, InferType, number, object, string } from "yup";
import TextInput from "../inputs/TextInput";
import SelectButtonInput from "../inputs/SelectButtonInput";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import useConfirm from "@/lib/confirm/useConfirm";
import { Link } from "@tanstack/react-router";
import classNames from "classnames";

const schema = object().shape({
    email: string().email().required(),
    first_name: string().required(),
    last_name: string().required(),
    phone_number: string().nullable(),
    address_1: string().nullable(),
    address_2: string().nullable(),
    city: string().nullable(),
    state: string().nullable(),
    zip: string().nullable(),
    account_type: string().required(),
    is_active: boolean().nullable(),
    organization_id: number().when("account_type", {
        is: (x: AccountType) => x === "grower" || x === "consultant",
        then: (schema) => schema.required().nonNullable(),
        otherwise: (schema) => schema.nullable().notRequired(),
    }),
    group_role: string().oneOf(["admin", "member"]).nullable(),
});

function AddEditUserForm({
    existingUser,
    onClose,
}: {
    existingUser?: User;
    onClose: (shouldRefetch: boolean) => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { user, userIsSentinel, isConsultant, isGrower } = useAuth();

    const isEdit = !!existingUser;
    const currentOrganization = user?.organization as Organization;

    const f = useForm<InferType<typeof schema>>({
        resolver: yupResolver(schema),
        defaultValues: {
            organization_id: undefined,
            phone_number: "",
            address_1: "",
            address_2: "",
            city: "",
            state: "",
            zip: "",
        },
    });

    const [accountType, organization_id] = f.watch([
        "account_type",
        "organization_id",
    ]);

    const accountTypeNeedsOrganization =
        accountType === "grower" || accountType === "consultant";

    const canEditOrganization = useMemo(() => {
        if (!accountTypeNeedsOrganization) return false;
        if (userIsSentinel) return true;
        if (isGrower) return false;
        if (isConsultant) return accountType == "grower";
        return false;
    }, [
        accountType,
        accountTypeNeedsOrganization,
        isConsultant,
        isGrower,
        userIsSentinel,
    ]);

    const organizationGroupType = useMemo(() => {
        return accountType === "consultant"
            ? "service provider"
            : accountType === "grower"
              ? "farm"
              : null;
    }, [accountType]);

    const organizationLabel = useMemo(() => {
        return accountType === "consultant" ? "Service Provider" : "Farm";
    }, [accountType]);

    const organizationPlaceholder = useMemo(() => {
        return accountType === "consultant"
            ? "Search existing service provider"
            : "Search existing organization";
    }, [accountType]);

    const { showConfirm } = useConfirm();

    useEffect(() => {
        if (existingUser) {
            const existingOrg = existingUser.organization;

            f.reset({
                email: existingUser.email,
                first_name: existingUser.first_name,
                last_name: existingUser.last_name,
                phone_number: existingUser.phone_number ?? "",
                address_1: existingUser.address_1 ?? "",
                address_2: existingUser.address_2 ?? "",
                city: existingUser.city ?? "",
                state: existingUser.state ?? "",
                zip: existingUser.zip ?? "",
                account_type: existingUser.account_type,
                is_active: existingUser.is_active,
                organization_id:
                    existingOrg && typeof existingOrg !== "string"
                        ? existingOrg.id
                        : undefined,
            });
        }
    }, [existingUser, f]);

    const organizationsQuery = useGetRequest<Organization[]>({
        url: "organizations/",
        enabled: canEditOrganization,
    });

    const selectedOrganization = useMemo(() => {
        return organizationsQuery.data?.find(
            (org) => org.id === Number(organization_id),
        );
    }, [organizationsQuery.data, organization_id]);

    useEffect(() => {
        if (selectedOrganization && existingUser) {
            f.setValue(
                "group_role",
                selectedOrganization.group_admins?.some(
                    (id) => id === existingUser?.id,
                )
                    ? "admin"
                    : "member",
            );
        }
    }, [selectedOrganization, existingUser, f]);

    useEffect(() => {
        if (
            !existingUser &&
            accountTypeNeedsOrganization &&
            !canEditOrganization
        ) {
            f.setValue("organization_id", currentOrganization?.id);
        }
    }, [
        accountTypeNeedsOrganization,
        canEditOrganization,
        currentOrganization?.id,
        existingUser,
        f,
    ]);

    const organizationOptions = useMemo(() => {
        if (!organizationGroupType) return [];

        return (organizationsQuery.data ?? [])
            .filter((org) => org.group_type === organizationGroupType)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((org) => ({
                id: org.id.toString(),
                description: org.name,
            }));
    }, [organizationsQuery.data, organizationGroupType]);

    const accountTypeOptions = useMemo(() => {
        const opts: {
            id: AccountType;
            description: string;
        }[] = [];

        const addOption = (id: AccountType, description: string) => {
            if (!opts.some((opt) => opt.id === id)) {
                opts.push({ id, description });
            }
        };

        if (isGrower && (isEdit || user?.is_group_admin)) {
            addOption("grower", "Teammate");
        }

        if (isConsultant) {
            if (isEdit || user?.is_group_admin) {
                addOption("consultant", "Teammate");
            }
            addOption("grower", "Grower");
        }

        if (userIsSentinel) {
            if (isEdit || user?.is_group_admin) {
                addOption("sentinel", "Teammate");
            }
            addOption("consultant", "Consultant");
            addOption("grower", "Grower");
        }

        return opts;
    }, [isConsultant, isEdit, isGrower, user?.is_group_admin, userIsSentinel]);

    useEffect(() => {
        if (!isEdit && !accountType && accountTypeOptions[0]) {
            f.setValue("account_type", accountTypeOptions[0].id);
        }
    }, [accountType, accountTypeOptions, f, isEdit]);

    const selectedAccountTypeLabel = useMemo(() => {
        if (!accountType) return "User";
        return (
            accountTypeOptions.find((opt) => opt.id === accountType)
                ?.description ?? "User"
        );
    }, [accountType, accountTypeOptions]);

    const createMutation = useMutateRequest({
        successMessage: "User invited",
        errorMessage: "Failed to invite user",
    });

    const updateMutation = useMutateRequest({
        successMessage: "User updated",
        errorMessage: "Failed to update user",
    });

    const onSubmit = (data: InferType<typeof schema>) => {
        const { organization_id, ...userPayload } = data;

        const basePayload = {
            ...userPayload,
            username: data.email,
        };

        const organizationPayload = canEditOrganization
            ? { organization_id }
            : {};

        if (isEdit && existingUser) {
            updateMutation.mutate(
                {
                    url: `users/${existingUser.id}/`,
                    method: "patch",
                    data: {
                        ...basePayload,
                        ...organizationPayload,
                    },
                },
                { onSuccess: () => onClose(true) },
            );
            return;
        }

        if (data.account_type === "sentinel") {
            createMutation.mutate(
                {
                    url: "users/sentinel/",
                    method: "post",
                    data: basePayload,
                },
                { onSuccess: () => onClose(true) },
            );
            return;
        }

        if (data.account_type === "consultant") {
            createMutation.mutate(
                {
                    url: "consultants/",
                    method: "post",
                    data: {
                        ...basePayload,
                        organization_id,
                    },
                },
                { onSuccess: () => onClose(true) },
            );
            return;
        }

        createMutation.mutate(
            {
                url: "growers/",
                method: "post",
                data: {
                    ...basePayload,
                    organization_id,
                },
            },
            { onSuccess: () => onClose(true) },
        );
    };

    const deactivateMutation = useMutateRequest({
        successMessage: "User deactivated",
        errorMessage: "Failed to deactivate user",
    });

    const activateMutation = useMutateRequest({
        successMessage: "User activated",
        errorMessage: "Failed to activate user",
    });

    const activateUser = () => {
        if (!existingUser) return;
        showConfirm({
            title: "Activate User",
            description: `Are you sure you want to activate ${existingUser.full_name}?`,
            color: "success",
            onConfirm: async () => {
                await activateMutation.mutateAsync(
                    {
                        url: `users/${existingUser.id}/activate/`,
                        method: "patch",
                    },
                    { onSuccess: () => onClose(true) },
                );
            },
        });
    };

    const deactivateUser = () => {
        if (!existingUser) return;
        showConfirm({
            title: "Deactivate User",
            description: `Are you sure you want to deactivate ${existingUser.full_name}?`,
            color: "danger",
            onConfirm: async () => {
                await deactivateMutation.mutateAsync(
                    {
                        url: `users/${existingUser.id}/deactivate/`,
                        method: "patch",
                    },
                    { onSuccess: () => onClose(true) },
                );
            },
        });
    };

    const org = existingUser?.organization;
    const orgId = org && typeof org !== "string" ? org.id : undefined;
    const orgDisplay = org && (typeof org === "string" ? org : org.name);

    return (
        <FormProvider {...f}>
            <Modal
                isOpen
                onClose={() => onClose(false)}
                isDismissable={false}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        {isEdit
                            ? `Edit ${selectedAccountTypeLabel}`
                            : `Invite ${selectedAccountTypeLabel}`}
                    </ModalHeader>
                    <ModalBody className="flex flex-col gap-2">
                        <SelectButtonInput
                            name="account_type"
                            label="Account Type"
                            options={accountTypeOptions}
                            orientation="horizontal"
                            classNames={{
                                base: "flex-1 w-full",
                                wrapper: classNames("grid", {
                                    "grid-cols-1":
                                        accountTypeOptions.length === 1,
                                    "grid-cols-2":
                                        accountTypeOptions.length === 2,
                                    "grid-cols-3":
                                        accountTypeOptions.length >= 3,
                                }),
                            }}
                            isDisabled={isEdit}
                        />
                        <div className="md:grid md:grid-cols-2 md:gap-2">
                            <TextInput
                                name="first_name"
                                label="First Name"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="last_name"
                                label="Last Name"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                        </div>
                        <div className="md:grid md:grid-cols-2 md:gap-2">
                            <TextInput
                                name="email"
                                label="Email"
                                type="email"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="phone_number"
                                label="Phone Number"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                        </div>
                        <div className="md:grid md:grid-cols-2 md:gap-2">
                            <TextInput
                                name="address_1"
                                label="Address 1"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="address_2"
                                label="Address 2"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <TextInput
                                name="city"
                                label="City"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="state"
                                label="State"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                            <TextInput
                                name="zip"
                                label="Zip"
                                labelPlacement="outside"
                                size="sm"
                                placeholder=" "
                            />
                        </div>
                        {canEditOrganization && (
                            <>
                                <div className="flex flex-row items-end gap-2">
                                    <div className="w-full sm:flex-1">
                                        <AutoCompleteInput
                                            name="organization_id"
                                            label={organizationLabel}
                                            options={organizationOptions}
                                            labelPlacement="outside"
                                            size="sm"
                                            placeholder={
                                                organizationPlaceholder
                                            }
                                            isLoading={
                                                organizationsQuery.isLoading
                                            }
                                        />
                                    </div>

                                    <Button
                                        as={Link}
                                        to="/dashboard/network/organizations/new"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="sm"
                                        variant="flat"
                                        color="primary"
                                    >
                                        Create New
                                    </Button>
                                </div>
                                <SelectButtonInput
                                    name="group_role"
                                    label="Role"
                                    size="sm"
                                    orientation="horizontal"
                                    classNames={{
                                        base: "flex-1 w-full",
                                        wrapper: "grid grid-cols-2",
                                    }}
                                    options={[
                                        { id: "admin", description: "Admin" },
                                        { id: "member", description: "Member" },
                                    ]}
                                />
                            </>
                        )}
                        {!isEdit &&
                            accountTypeNeedsOrganization &&
                            !canEditOrganization &&
                            currentOrganization && (
                                <div className="flex flex-col gap-1">
                                    <div className="text-xs text-gray-500">
                                        {organizationLabel}
                                    </div>
                                    <div className="text-sm">
                                        {currentOrganization.name}
                                    </div>
                                </div>
                            )}
                        {isEdit &&
                            accountTypeNeedsOrganization &&
                            !canEditOrganization &&
                            orgDisplay && (
                                <div className="flex flex-col gap-1">
                                    <div className="text-xs text-gray-500">
                                        {organizationLabel}
                                    </div>
                                    {orgId ? (
                                        <Link
                                            to="/dashboard/network/organizations/$organizationId"
                                            params={{
                                                organizationId: orgId.toString(),
                                            }}
                                            className="text-sm text-primary-500 hover:underline"
                                        >
                                            {orgDisplay}
                                        </Link>
                                    ) : (
                                        <div className="text-sm">
                                            {orgDisplay}
                                        </div>
                                    )}
                                </div>
                            )}

                        <Divider />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            size="sm"
                            variant="flat"
                            onPress={() => onClose(false)}
                            fullWidth
                            isDisabled={
                                createMutation.isPending ||
                                updateMutation.isPending ||
                                deactivateMutation.isPending ||
                                activateMutation.isPending
                            }
                        >
                            Cancel
                        </Button>

                        {isEdit &&
                            userIsSentinel &&
                            existingUser?.is_active && (
                                <Button
                                    size="sm"
                                    color="danger"
                                    fullWidth
                                    onPress={deactivateUser}
                                    isLoading={deactivateMutation.isPending}
                                >
                                    Deactivate
                                </Button>
                            )}

                        {isEdit &&
                            userIsSentinel &&
                            existingUser &&
                            !existingUser.is_active && (
                                <Button
                                    size="sm"
                                    color="success"
                                    fullWidth
                                    onPress={activateUser}
                                    isLoading={activateMutation.isPending}
                                >
                                    Activate
                                </Button>
                            )}

                        <Button
                            size="sm"
                            color="primary"
                            onPress={() => {
                                f.trigger();
                                f.handleSubmit(onSubmit)();
                            }}
                            fullWidth
                            isLoading={
                                createMutation.isPending ||
                                updateMutation.isPending
                            }
                        >
                            {isEdit ? "Update" : "Invite"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </FormProvider>
    );
}

export default AddEditUserForm;
